import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { CalendarDays, ClipboardList, GripVertical, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, formatApiErrorDetail } from "@/lib/api";
import { HomeworkEmailSetting } from "@/components/HomeworkEmailSetting";

const iso = (d) => format(d, "yyyy-MM-dd");
const toDate = (s) => new Date(`${s}T00:00:00`);

const ChangeDate = ({ a, onMove }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`homework-change-date-${a.id}`}
          className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-0.5 text-[11px] text-primary transition-colors hover:border-primary">
          <CalendarClock size={11} /> Change date
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" data-testid={`homework-change-date-popover-${a.id}`}>
        <Calendar mode="single" selected={toDate(a.due_date)} defaultMonth={toDate(a.due_date)} onSelect={(d) => { if (d) { setOpen(false); onMove(a, iso(d)); } }} />
      </PopoverContent>
    </Popover>
  );
};

const DayList = ({ day, items, onMove }) => (
  <div className="min-w-0 flex-1" data-testid="homework-day-list">
    <p className="font-mono text-[11px] uppercase tracking-widest text-primary" data-testid="homework-day-label">Due {format(day, "EEEE d MMMM")}</p>
    {items.length === 0 ? (
      <p className="mt-3 text-sm text-muted-foreground" data-testid="homework-day-empty">No homework due on this day.</p>
    ) : (
      <>
        <ul className="mt-3 space-y-2">
          {items.map((a) => {
            const all = a.total_students > 0 && a.completed_count === a.total_students;
            return (
              <li key={a.id} draggable onDragStart={(e) => { e.dataTransfer.setData("text/plain", a.id); e.dataTransfer.effectAllowed = "move"; }}
                className="cursor-grab rounded-xl border border-border bg-background px-4 py-3 transition-shadow hover:shadow-md active:cursor-grabbing" data-testid={`homework-day-item-${a.id}`}>
                <p className="flex items-start gap-2 text-sm font-medium"><GripVertical size={15} className="mt-0.5 hidden shrink-0 text-muted-foreground md:block" /><ClipboardList size={15} className="mt-0.5 shrink-0 text-primary" /> {a.title}</p>
                <p className="mt-1 flex flex-wrap items-center gap-2 pl-6 text-xs text-muted-foreground md:pl-12">
                  <span data-testid={`homework-day-class-${a.id}`}>{a.class_name}</span>
                  <span className={`rounded-full px-2 py-0.5 ${all ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-secondary"}`} data-testid={`homework-day-progress-${a.id}`}>
                    {a.completed_count}/{a.total_students} finished
                  </span>
                  <ChangeDate a={a} onMove={onMove} />
                </p>
              </li>
            );
          })}
        </ul>
        <p className="mt-3 hidden text-xs text-muted-foreground md:block">Tip: drag a piece of homework onto another day to move its due date.</p>
      </>
    )}
  </div>
);

// Month view of every homework due date across the teacher's classes; tap a day to see what's due, drag to move.
export const HomeworkCalendar = ({ refreshKey, onMoved }) => {
  const [items, setItems] = useState(null);
  const [day, setDay] = useState(new Date());
  const [month, setMonth] = useState(new Date());
  const [over, setOver] = useState(null);
  useEffect(() => { api.get("/teacher/homework-calendar").then(({ data }) => setItems(data.assignments || [])).catch(() => setItems([])); }, [refreshKey]);
  const byDay = useMemo(() => (items || []).reduce((m, a) => ({ ...m, [a.due_date]: [...(m[a.due_date] || []), a] }), {}), [items]);
  if (items === null) return null;

  const move = async (a, to) => {
    if (to === a.due_date) return;
    try {
      await api.patch(`/classes/${a.class_id}/assignments/${a.id}`, { due_date: to });
      setItems((list) => list.map((x) => (x.id === a.id ? { ...x, due_date: to } : x)).sort((x, y) => x.due_date.localeCompare(y.due_date)));
      setDay(toDate(to));
      setMonth(toDate(to));
      onMoved?.();
      toast.success(`Moved to ${format(toDate(to), "EEEE d MMMM")}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not move the due date");
    }
  };
  const dropDay = (e) => e.target.closest?.("[data-date]")?.getAttribute("data-date");
  const onDrop = (e) => {
    e.preventDefault();
    const to = dropDay(e), a = items.find((x) => x.id === e.dataTransfer.getData("text/plain"));
    setOver(null);
    if (to && a) move(a, to);
  };

  const dueDays = Object.keys(byDay).map(toDate);
  const upcoming = items.filter((a) => a.due_date >= iso(new Date())).length;
  const DayContent = ({ date }) => (
    <span data-date={iso(date)} data-testid={`homework-cal-day-${iso(date)}`}
      className={`flex h-full w-full items-center justify-center rounded-md ${over === iso(date) ? "bg-primary/25 ring-2 ring-primary" : ""}`}>{date.getDate()}</span>
  );
  return (
    <section className="mt-8 rounded-2xl border border-border bg-card p-5" data-testid="homework-calendar">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-serif text-xl"><CalendarDays size={18} className="text-primary" /> Homework calendar</h2>
        <p className="text-xs text-muted-foreground" data-testid="homework-calendar-upcoming">{upcoming} piece{upcoming === 1 ? "" : "s"} of homework still to come</p>
      </div>
      <div className="mt-4 flex flex-col gap-6 md:flex-row md:items-start">
        <div onDragOver={(e) => { e.preventDefault(); setOver(dropDay(e) || null); }} onDragLeave={() => setOver(null)} onDrop={onDrop}
          className="shrink-0 self-center md:self-start" data-testid="homework-calendar-dropzone">
          <Calendar mode="single" selected={day} onSelect={(d) => d && setDay(d)} data-testid="homework-calendar-month" month={month} onMonthChange={setMonth} components={{ DayContent }}
            modifiers={{ due: dueDays }} modifiersClassNames={{ due: "font-bold text-primary underline decoration-2 underline-offset-4" }}
            className="rounded-xl border border-border bg-background" />
        </div>
        <DayList day={day} items={byDay[iso(day)] || []} onMove={move} />
      </div>
      {items.length === 0 && <p className="mt-3 text-xs text-muted-foreground" data-testid="homework-calendar-empty">Add a due date when you set homework and it will show up here.</p>}
      <HomeworkEmailSetting />
    </section>
  );
};
